import { Callout, EmptyStateNoResults, ExternalLink, Pagination, type PrimaryPageAction, SearchInput, SimpleDataTable } from "@octopusdeploy/design-system-components";
import { useMutation, useQuery } from "@octopusdeploy/octopus-react-client";
import type { ExternalFeedResource, FeedResource, Repository, ResourceCollection } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { useCallback } from "react";
import type { ActionEvent } from "~/analytics/Analytics";
import { Action, useAnalyticActionDispatch } from "~/analytics/Analytics";
import { BorderedContent } from "~/components/BorderedContent/BorderedContent";
import { PortalEmptyStateOnboarding } from "~/components/GettingStarted/PortalEmptyStateOnboarding";
import InternalLink from "~/components/Navigation/InternalLink/InternalLink";
import { OverflowMenu, OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { PageContent } from "~/components/PageContent/PageContent";
import PermissionCheck, { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
export interface ExternalFeedsPageProps {
    spaceId: string;
    loaderData: ExternalFeedsPageLoaderData;
    queryParams: ExternalFeedsPageQueryParams;
    setQueryParams: QueryParamValuesSetter<ExternalFeedsPageQueryParams>;
}
export interface ExternalFeedsPageQueryParams {
    page: number | undefined;
    pageSize: number | undefined;
    feedName: string;
}
export async function externalFeedsPageLoader(repository: Repository, queryParams: ExternalFeedsPageQueryParams): Promise<ExternalFeedsPageLoaderData> {
    return { feeds: await getPaginatedExternalFeeds(repository, queryParams.page, queryParams.pageSize, queryParams.feedName), unfilteredTotalFeeds: (await repository.Feeds.listExternal({ skip: 0, take: 0 })).TotalResults };
}
interface ExternalFeedsPageLoaderData {
    feeds: ResourceCollection<ExternalFeedResource>;
    unfilteredTotalFeeds: number;
}
const externalFeedsPageTitle = "External Feeds";
const defaultPageSize = 30;
const pageSizeOptions = [defaultPageSize, 50, 100];
export function ExternalFeedsPage({ spaceId, loaderData, queryParams, setQueryParams }: ExternalFeedsPageProps) {
    const dispatchAction = useAnalyticActionDispatch();
    const [debouncedFeedName, setDebouncedFeedName] = useDebouncedExternalState(queryParams.feedName, (value) => setQueryParams({ feedName: value, page: 1 }), 200);
    const { result, refetch: refetchFeeds } = usePaginatedExternalFeeds(loaderData, queryParams);
    const { feeds, unfilteredTotalFeeds } = result;
    const onPageChange = useCallback((page: number) => {
        setQueryParams({ ...queryParams, page });
    }, [queryParams, setQueryParams]);
    const onPageSizeChange = useCallback((pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize: pageSize, page: 1 });
    }, [queryParams, setQueryParams]);
    const addFeedClicked = () => {
        const ev: ActionEvent = {
            action: Action.Add,
            resource: "External Feed",
        };
        dispatchAction("Add Feed", ev);
    };
    const addFeedPageAction: PrimaryPageAction = {
        type: "navigate",
        hasPermissions: isAllowed({ permission: Permission.FeedEdit }),
        label: "Add Feed",
        path: links.createFeedPage.generateUrl({ spaceId }),
        onClick: addFeedClicked,
    };
    if (unfilteredTotalFeeds === 0) {
        return (<PortalEmptyStateOnboarding title={"Add a feed to integrate with an external package repository"} description={"Octopus can consume packages from TeamCity, Azure DevOps, Docker Registry, MyGet, Maven, locally or hosted remotely."} learnMore={<ExternalLink href="OnboardingPackageRepositoriesLearnMore">Learn more</ExternalLink>} primaryAction={addFeedPageAction}/>);
    }
    const hideBottomPagination = feeds.Items.length < 20;
    return (<PageContent header={{ title: externalFeedsPageTitle, primaryAction: addFeedPageAction }} filters={{
            inputs: [<SearchInput autoFocus={true} value={debouncedFeedName} onChange={setDebouncedFeedName} accessibleName={"Search by feed name"} placeholder={"Search by name"}/>],
        }} pagination={{
            ui: (<Pagination label={"External Feeds"} totalResults={feeds.TotalResults} itemsPerPageOptions={pageSizeOptions} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} selectedItemsPerPage={feeds.ItemsPerPage}/>),
            placement: hideBottomPagination ? "top" : "topAndBottom",
        }}>
            <BorderedContent>
                {feeds.TotalResults === 0 ? (<EmptyStateNoResults />) : (<SimpleDataTable data={feeds.Items} columns={[
                { title: "Name", render: (feed) => <InternalLink to={links.externalFeedPage.generateUrl({ spaceId, feedId: feed.Id })}>{feed.Name}</InternalLink> },
                { title: "URL", render: (feed) => <ExternalLink href={feed.FeedUri}>{feed.FeedUri}</ExternalLink> },
                { title: "", columnSize: "x-small", render: (feed) => <ExternalFeedOverflow spaceId={spaceId} feed={feed} onFeedDeleted={refetchFeeds}/> },
            ]} getRowKey={(feed) => feed.Id}/>)}
            </BorderedContent>
        </PageContent>);
}
interface ExternalFeedOverflowProps {
    spaceId: string;
    feed: FeedResource;
    onFeedDeleted: () => void;
}
function ExternalFeedOverflow(props: ExternalFeedOverflowProps) {
    const { execute: deleteFeed } = useMutation({
        name: "Delete feed",
        action: (repository, feed: FeedResource) => {
            return repository.Feeds.del(feed);
        },
        afterComplete: async () => props.onFeedDeleted(),
    });
    return (<PermissionCheck permission={Permission.FeedEdit} wildcard={true}>
            <OverflowMenu menuItems={[
            // normally, we wouldn't have an Edit/Delete menu item here, but we've had
            // customers confused how to edit an item (due to the prominence of the feed url)
            OverflowMenuItems.navItem("Edit", links.externalFeedPage.generateUrl({ spaceId: props.spaceId, feedId: props.feed.Id })),
            OverflowMenuItems.deleteItemDefault("feed", () => deleteFeed(props.feed), { permission: [Permission.AdministerSystem, Permission.FeedEdit] }, undefined, <Callout title={"In-use feeds must not be deleted"} type={"warning"}>
                            {`If you are using Config as Code, please check your deployment and runbook OCL files for steps with 'packages' fields referring to '${props.feed.Name}' before deleting.`}
                        </Callout>),
        ]}/>
        </PermissionCheck>);
}
function getPaginatedExternalFeeds(repository: Repository, page: number = 1, pageSize: number = defaultPageSize, partialName: string = "") {
    return repository.Feeds.listExternal({
        skip: (page - 1) * pageSize,
        take: pageSize,
        partialName,
    });
}
function usePaginatedExternalFeeds(initialResult: ExternalFeedsPageLoaderData, queryParams: ExternalFeedsPageQueryParams) {
    const { page, pageSize, feedName } = queryParams;
    return useQuery(async (repository) => {
        const feeds = getPaginatedExternalFeeds(repository, page, pageSize, feedName);
        const unfilteredTotalFeeds = repository.Feeds.listExternal({ skip: 0, take: 0 });
        return { feeds: await feeds, unfilteredTotalFeeds: (await unfilteredTotalFeeds).TotalResults };
    }, [page, pageSize, feedName], "Get paginated external feeds", {
        initialResult: { feeds: initialResult.feeds, unfilteredTotalFeeds: initialResult.unfilteredTotalFeeds },
    });
}
